import React from "react";
import DashboardHeader from "../components/dashboard/DashboardHeader";
import { useNavigate } from "react-router-dom";
import StatCard from "../components/dashboard/StatCard";
import QuickActionButton from "../components/dashboard/QuickActionButton";
import SalesChart from "../components/dashboard/SalesChart";
import RecentBookings from "../components/dashboard/RecentBookings";
import TopStylists from "../components/dashboard/TopStylists";
import {
  Users,
  Scissors,
  UserCheck,
  Calendar,
  ShoppingCart,
  Settings,
  CreditCard,
  LayoutGrid,
} from "lucide-react";
import { getDashboard, DashboardResponse } from "../services/dashboard";
import UpcomingBookings from "../components/dashboard/UpCominBookings";
import AppointmentSidebar from "../components/AppointmentSidebar";
import GlobalLoader from "../components/ui/GlobalLoader";
import { useToast } from "../hooks/useToast";
import { useEmployees } from "../hooks/useEmployees";
import { useAuth } from "../auth/AuthContext";

const Dashboard = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [dashboard, setDashboard] = React.useState<DashboardResponse | null>(
    null
  );
  const [loading, setLoading] = React.useState(true);
  const [dateRange, setDateRange] = React.useState({
    startDate: new Date(new Date().setMonth(new Date().getMonth() - 1))
      .toISOString()
      .split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
  });
  const [isAppointmentSidebarOpen, setIsAppointmentSidebarOpen] =
    React.useState(false);
  const [appointmentSidebarMode, setAppointmentSidebarMode] = React.useState<
    "Services" | "Sunbeds"
  >("Services");

  const { staffList } = useEmployees();
  const { user } = useAuth();
  const currencySymbol = user?.company?.currency_symbol || user?.company?.currency?.currency_symbol || "£";

  const handleQuickAction = (action: string) => {
    switch (action) {
      case "Add Sale":
        setAppointmentSidebarMode("Services");
        setIsAppointmentSidebarOpen(true);
        break;
      case "Add Sunbed Sale":
        setAppointmentSidebarMode("Sunbeds");
        setIsAppointmentSidebarOpen(true);
        break;
      case "POS":
        navigate("/pos");
        break;
      case "Calendar":
        navigate("/calendar");
        break;
      case "Bookings":
        navigate("/bookings");
        break;
      case "Services":
        navigate("/manage/services");
        break;
      case "Store":
        navigate("/manage/products");
        break;
      case "Settings":
        navigate("/settings");
        break;
      default:
        console.log(`No route defined for ${action}`);
    }
  };

  React.useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await getDashboard(dateRange.startDate, dateRange.endDate);
        setDashboard(res);

      } catch (err) {
        console.error("Dashboard failed:", err);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    })();
  }, [dateRange]);


  return (
    <div className="min-h-screen w-full bg-[#F9F9F9] text-[#1E1E1E] font-inter p-2 md:py-6 md:px-3 border">
      <div className="px-2 md:px-3 space-y-8">
        {/* Header - Kept outside of loading to maintain state */}
        <DashboardHeader
          onQuickAction={handleQuickAction}
          onDateChange={(start, end) =>
            setDateRange({ startDate: start, endDate: end })
          }
          currencySymbol={currencySymbol}
        />

        {loading ? (
          <GlobalLoader
            initialPageLoad={true}
            description="Loading dashboard statistics..."
          />
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard
                icon={Users}
                label="Employees"
                value={dashboard?.totalEmployees ?? "—"}
              />
              <StatCard
                icon={Scissors}
                label="Services"
                value={dashboard?.totalServices ?? "—"}
              />
              <StatCard
                icon={UserCheck}
                label="Customers"
                value={dashboard?.totalCustomers ?? "—"}
              />
              <StatCard
                icon={Calendar}
                label="Bookings"
                value={dashboard?.totalBooking ?? "—"}
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
              {/* LEFT SIDE */}
              <div className="space-y-6 h-full flex flex-col">
                <div>
                  <h2 className="text-xl font-semibold text-[#402B69] mb-4">
                    Quick Actions
                  </h2>

                  <div className="grid grid-cols-3 sm:grid-cols-3 lg:grid-cols-3 gap-3">
                    <QuickActionButton
                      icon={LayoutGrid}
                      label="POS"
                      onClick={() => handleQuickAction("POS")}
                    />
                    <QuickActionButton
                      icon={Calendar}
                      label="Calendar"
                      onClick={() => handleQuickAction("Calendar")}
                    />
                    <QuickActionButton
                      icon={Calendar}
                      label="Bookings"
                      onClick={() => handleQuickAction("Bookings")}
                    />
                    <QuickActionButton
                      icon={Scissors}
                      label="Services"
                      onClick={() => handleQuickAction("Services")}
                    />
                    <QuickActionButton
                      icon={ShoppingCart}
                      label="Store"
                      onClick={() => handleQuickAction("Store")}
                    />
                    <QuickActionButton
                      icon={Settings}
                      label="Settings"
                      onClick={() => handleQuickAction("Settings")}
                    />
                  </div>
                </div>

                {/* Graph */}
                <div className="flex-1">
                  <SalesChart
                    labels={dashboard?.topSaleLabels ?? []}
                    data={dashboard?.topSaleData ?? []}
                    currencySymbol={currencySymbol}
                  />
                </div>
              </div>
              <div className="h-full flex flex-col">
                <UpcomingBookings currencySymbol={currencySymbol} />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <RecentBookings
                currencySymbol={currencySymbol}
                bookings={
                  dashboard?.recentSales.map((sale) => {
                    // Extract staff name from the first item's employee
                    const staffName = sale.items?.[0]?.employee?.name ?? "No Staff Assigned";

                    // Extract time from date_time
                    const time = sale.date_time
                      ? new Date(sale.date_time).toLocaleTimeString("en-GB", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })
                      : "N/A";

                    return {
                      id: sale.id,
                      customer: sale.user.name,
                      email: sale.user.email || "N/A",
                      mobile: sale.user.formatted_mobile || sale.user.mobile || "N/A",
                      staff: staffName,
                      date: new Date(sale.date_time).toLocaleDateString("en-GB", {
                        day: "2-digit",
                        month: "short",
                        year: "numeric",
                      }),
                      time: time,
                      status: sale.status,
                      amount: sale.formated_amount_to_pay,
                    };
                  }) ?? []
                }
              />
              <TopStylists
                stylists={
                  dashboard?.stylistsChartData.labels.map((name, i) => ({
                    name,
                    thisMonth: dashboard.stylistsChartData.earningsData[i] ?? 0,
                    total: dashboard.stylistsChartData.earningsData[i] ?? 0,
                  })) ?? []
                }
                currencySymbol={currencySymbol}
              />
            </div>
          </>
        )}
      </div>

      {/* Appointment Sidebar */}
      <AppointmentSidebar
        isOpen={isAppointmentSidebarOpen}
        onClose={() => {
          setIsAppointmentSidebarOpen(false);
          // Reset to Services mode when closing
          setAppointmentSidebarMode("Services");
        }}
        staffList={staffList}
        initialServiceType={appointmentSidebarMode}
        mode="dashboard"
      />
    </div>
  );
};

export default Dashboard;
